import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { useState } from 'react';

import { isValidLink } from './supportLinks';

import { KeyboardAwareActionsheet } from '@/components/KeyboardAwareActionsheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type AddLinkSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
};

/**
 * Asks for one link to add to the next support message. It is the inquiry
 * chat's Add references sheet cut down: one field and no formatting call, so
 * the link goes out as the author typed it and the thread draws it as a link.
 * Another link is one more trip through the attach menu.
 *
 * A modal on web and a keyboard-aware sheet on native, so the field stays
 * above the keyboard.
 */
export function AddLinkSheet({ isOpen, onClose, onAdd }: AddLinkSheetProps) {
  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <ModalBackdrop />

        <ModalContent>
          <ModalHeader>
            <Heading size="md" className="text-typography-900">
              Add a link
            </Heading>
            <ModalCloseButton
              onPress={onClose}
              {...getAccessibilityProps({ role: 'button', label: 'Close' })}>
              <Icon as={X} size="md" className="text-typography-700" />
            </ModalCloseButton>
          </ModalHeader>

          <ModalBody className="pb-2 pt-4">
            {isOpen ? <AddLinkForm onClose={onClose} onAdd={onAdd} /> : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <KeyboardAwareActionsheet isOpen={isOpen} onClose={onClose} closeLabel="Close">
      <VStack className="w-full gap-4">
        <Heading size="md" className="text-typography-900">
          Add a link
        </Heading>

        {isOpen ? <AddLinkForm onClose={onClose} onAdd={onAdd} /> : null}
      </VStack>
    </KeyboardAwareActionsheet>
  );
}

/**
 * Mounted only while the sheet is open, so each opening starts with an empty
 * field. An address is flagged only once it is complete enough to judge, at
 * Add, rather than on every keystroke.
 */
function AddLinkForm({ onClose, onAdd }: Pick<AddLinkSheetProps, 'onClose' | 'onAdd'>) {
  const [value, setValue] = useState('');

  const [showError, setShowError] = useState(false);

  const trimmed = value.trim();

  const submit = () => {
    if (!isValidLink(trimmed)) {
      setShowError(true);

      return;
    }

    onAdd(trimmed);
  };

  return (
    <VStack className="w-full gap-4">
      <Text className="text-sm text-typography-700">
        Paste the address of an article, trial or web page to send with your message.
      </Text>

      <VStack className="gap-1">
        <Input size="md" isInvalid={showError}>
          <InputField
            value={value}
            onChangeText={(next) => {
              setValue(next);

              setShowError(false);
            }}
            onSubmitEditing={submit}
            placeholder="https://..."
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus
            keyboardType="url"
            returnKeyType="done"
            {...getAccessibilityProps({ label: 'Link address' })}
          />
        </Input>

        {showError ? (
          <Text size="xs" className="text-error-600" accessibilityLiveRegion="polite">
            Enter a web address that starts with https:// or www.
          </Text>
        ) : null}
      </VStack>

      <HStack className="gap-3">
        <Button variant="outline" size="md" onPress={onClose} className="flex-1">
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button size="md" onPress={submit} isDisabled={trimmed.length === 0} className="flex-1">
          <ButtonText>Add link</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
